import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Paintbrush, Type, Smile, Eye, LayoutGrid, Camera, Sparkles, AlignCenter, AlignLeft, AlignJustify, Bold, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { AnnouncementFormData, CustomTemplateSettings, TextAlignment, LayoutFamily, HeadlineFont, BodyFont, EmphasisStyle, PhotoLayout, PhotoShape, BackgroundStyle, DecorationToggles } from '@/types/announcement';
import { LayoutFamilyPicker } from './LayoutFamilyPicker';

type EditorPanel = 'layout' | 'background' | 'text' | 'photos' | 'decor' | 'emoji' | null;

interface TemplateEditorDrawerProps {
  formData: AnnouncementFormData;
  updateFormData: (updates: Partial<AnnouncementFormData>) => void;
  isEditing: boolean;
  onTogglePreview: () => void;
}

const BG_SOLID_COLORS = [
  '#fdf6e3', '#fff7ed', '#fef2f2', '#ecfdf5', '#eff6ff',
  '#faf5ff', '#fefce8', '#f0fdf4', '#fce7f3', '#e0e7ff',
  '#1a1a2e', '#0f172a', '#18181b', '#2d1b69', '#4a1942',
  '#1b4332', '#3d0c02', '#ffffff', '#f5f5f5', '#d1fae5',
];

const BG_GRADIENTS = [
  { label: 'Sunset', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { label: 'Ocean', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { label: 'Forest', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
  { label: 'Golden', value: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' },
  { label: 'Night', value: 'linear-gradient(135deg, #0c3547 0%, #1a1a2e 100%)' },
  { label: 'Rose', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
  { label: 'Mint', value: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)' },
  { label: 'Lavender', value: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)' },
];

const FONT_COLOR_PRESETS = [
  '#1a1a2e', '#2d1b69', '#4a1942', '#1b4332', '#3d0c02',
  '#ffffff', '#f5f5f5', '#e2e8f0', '#ef4444', '#f59e0b',
  '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4',
];

// ====== LAYOUT PANEL ======
function LayoutPanel({ formData, updateSettings, updateFormData }: {
  formData: AnnouncementFormData;
  updateSettings: (u: Partial<CustomTemplateSettings>) => void;
  updateFormData: (u: Partial<AnnouncementFormData>) => void;
}) {
  const cs = formData.customSettings;

  return (
    <div className="space-y-5">
      <LayoutFamilyPicker
        selected={cs.layoutFamily}
        onSelect={(family, defaults) => {
          updateFormData({
            customSettings: { ...cs, ...defaults, layoutFamily: family },
          });
        }}
      />

      {/* Section toggles */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Show / Hide Sections</Label>
        <div className="grid grid-cols-2 gap-2">
          {([
            { key: 'title' as const, label: 'Title' },
            { key: 'recipientName' as const, label: 'Recipient Name' },
            { key: 'fromLine' as const, label: 'From Line' },
            { key: 'body' as const, label: 'Message Body' },
            { key: 'pullQuote' as const, label: 'Pull Quote' },
            { key: 'signOff' as const, label: 'Sign Off' },
            { key: 'date' as const, label: 'Date' },
            { key: 'subtitle' as const, label: 'Subtitle' },
            { key: 'photoCaption' as const, label: 'Photo Caption' },
            { key: 'stickerRow' as const, label: 'Sticker Row' },
          ] as const).map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch
                checked={cs.sections[key]}
                onCheckedChange={(checked) => updateSettings({ sections: { ...cs.sections, [key]: checked } })}
                className="scale-75"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ====== BACKGROUND PANEL ======
function BackgroundPanel({ formData, updateSettings }: {
  formData: AnnouncementFormData;
  updateSettings: (u: Partial<CustomTemplateSettings>) => void;
}) {
  const cs = formData.customSettings;

  const BG_STYLES: { value: BackgroundStyle; label: string }[] = [
    { value: 'solid', label: 'Solid' },
    { value: 'gradient', label: 'Gradient' },
    { value: 'textured_paper', label: 'Paper' },
    { value: 'dark_dramatic', label: 'Dark' },
    { value: 'romantic_warm', label: 'Warm' },
    { value: 'vignette', label: 'Watercolor' },
  ];

  return (
    <div className="space-y-4">
      {/* Background Style */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Style</Label>
        <div className="flex flex-wrap gap-1.5">
          {BG_STYLES.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => {
                const updates: Partial<CustomTemplateSettings> = { backgroundStyle: value };
                if (value === 'dark_dramatic') updates.fontColor = '#ffffff';
                updateSettings(updates);
              }}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                cs.backgroundStyle === value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Color picker for solid / gradient */}
      {(cs.backgroundStyle === 'solid' || cs.backgroundStyle === 'gradient') && (
        <>
          {cs.backgroundStyle === 'solid' ? (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Color</Label>
              <div className="flex flex-wrap gap-2">
                {BG_SOLID_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => updateSettings({ backgroundColor: color })}
                    className={cn(
                      'w-8 h-8 rounded-full border-2 transition-all active:scale-95',
                      cs.backgroundColor === color ? 'border-primary ring-2 ring-primary/30 scale-110' : 'border-border'
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
                <label className="w-8 h-8 rounded-full border-2 border-dashed border-border flex items-center justify-center cursor-pointer relative overflow-hidden">
                  <span className="text-xs text-muted-foreground">+</span>
                  <input type="color" value={cs.backgroundColor.startsWith('#') ? cs.backgroundColor : '#fdf6e3'}
                    onChange={(e) => updateSettings({ backgroundColor: e.target.value })}
                    className="absolute inset-0 opacity-0 cursor-pointer" />
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Gradient</Label>
              <div className="grid grid-cols-4 gap-2">
                {BG_GRADIENTS.map((g) => (
                  <button
                    key={g.label}
                    onClick={() => updateSettings({ backgroundColor: g.value })}
                    className={cn(
                      'h-10 rounded-lg border-2 transition-all active:scale-95',
                      cs.backgroundColor === g.value ? 'border-primary ring-2 ring-primary/30' : 'border-border'
                    )}
                    style={{ background: g.value }}
                    title={g.label}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ====== TEXT PANEL ======
function TextPanel({ formData, updateSettings }: {
  formData: AnnouncementFormData;
  updateSettings: (u: Partial<CustomTemplateSettings>) => void;
}) {
  const cs = formData.customSettings;

  const HEADLINE_FONTS: { value: HeadlineFont; label: string; cls: string }[] = [
    { value: 'elegant_serif', label: 'Elegant', cls: 'font-serif' },
    { value: 'modern_sans', label: 'Modern', cls: 'font-sans' },
    { value: 'handwritten_script', label: 'Script', cls: 'italic font-serif' },
    { value: 'bold_editorial', label: 'Bold', cls: 'font-sans font-black uppercase' },
  ];

  const BODY_FONTS: { value: BodyFont; label: string; cls: string }[] = [
    { value: 'clean_serif', label: 'Serif', cls: 'font-serif' },
    { value: 'clean_sans', label: 'Sans', cls: 'font-sans' },
  ];

  const EMPHASIS_OPTIONS: { value: EmphasisStyle; label: string }[] = [
    { value: 'none', label: 'None' },
    { value: 'italic_pullquote', label: 'Pull Quote' },
    { value: 'oversized_title', label: 'Big Title' },
    { value: 'quote_block', label: 'Quote Block' },
    { value: 'highlighted_phrase', label: 'Highlight' },
  ];

  return (
    <div className="space-y-5">
      {/* Headline Font */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Headline Style</Label>
        <div className="grid grid-cols-4 gap-1.5">
          {HEADLINE_FONTS.map(({ value, label, cls }) => (
            <button key={value} onClick={() => updateSettings({ headlineFont: value })}
              className={cn('flex flex-col items-center gap-1 p-2 rounded-lg border transition-all',
                cs.headlineFont === value ? 'border-primary bg-primary/5' : 'border-border')}>
              <span className={cn('text-base', cls)}>Aa</span>
              <span className="text-[9px] text-muted-foreground">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Body Font */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Body Font</Label>
        <div className="grid grid-cols-2 gap-2">
          {BODY_FONTS.map(({ value, label, cls }) => (
            <button key={value} onClick={() => updateSettings({ bodyFont: value })}
              className={cn('flex items-center justify-center gap-2 p-2.5 rounded-lg border transition-all',
                cs.bodyFont === value ? 'border-primary bg-primary/5' : 'border-border')}>
              <span className={cn('text-sm', cls)}>Body text</span>
              <span className="text-[9px] text-muted-foreground">({label})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Font Color */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Text Color</Label>
        <div className="flex flex-wrap gap-1.5">
          {FONT_COLOR_PRESETS.map((color) => (
            <button key={color} onClick={() => updateSettings({ fontColor: color })}
              className={cn('w-7 h-7 rounded-full border-2 transition-all active:scale-95',
                cs.fontColor === color ? 'border-primary ring-2 ring-primary/30 scale-110' : 'border-border')}
              style={{ backgroundColor: color }} />
          ))}
          <label className="w-7 h-7 rounded-full border-2 border-dashed border-border flex items-center justify-center cursor-pointer relative overflow-hidden">
            <span className="text-[8px] text-muted-foreground">+</span>
            <input type="color" value={cs.fontColor} onChange={(e) => updateSettings({ fontColor: e.target.value })}
              className="absolute inset-0 opacity-0 cursor-pointer" />
          </label>
        </div>
      </div>

      {/* Alignment */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Alignment</Label>
        <div className="flex gap-2">
          {([
            { value: 'center' as TextAlignment, icon: AlignCenter, label: 'Center' },
            { value: 'left' as TextAlignment, icon: AlignLeft, label: 'Left' },
            { value: 'mixed' as TextAlignment, icon: AlignJustify, label: 'Mixed' },
          ]).map(({ value, icon: Icon, label }) => (
            <button key={value} onClick={() => updateSettings({ textAlignment: value })}
              className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-all flex-1',
                cs.textAlignment === value ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground')}>
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Bold Title + Emphasis */}
      <div className="flex items-center justify-between">
        <Label className="text-sm text-muted-foreground flex items-center gap-2">
          <Bold className="h-3.5 w-3.5" /> Bold Title
        </Label>
        <Switch checked={cs.titleBold} onCheckedChange={(checked) => updateSettings({ titleBold: checked })} />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Emphasis Style</Label>
        <div className="flex flex-wrap gap-1.5">
          {EMPHASIS_OPTIONS.map(({ value, label }) => (
            <button key={value} onClick={() => updateSettings({ emphasisStyle: value })}
              className={cn('px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                cs.emphasisStyle === value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ====== PHOTOS PANEL ======
function PhotosPanel({ formData, updateSettings }: {
  formData: AnnouncementFormData;
  updateSettings: (u: Partial<CustomTemplateSettings>) => void;
}) {
  const cs = formData.customSettings;

  const PHOTO_LAYOUTS: { value: PhotoLayout; label: string; emoji: string }[] = [
    { value: 'hero', label: 'Hero', emoji: '🖼️' },
    { value: 'side_by_side_circles', label: 'Circles', emoji: '⭕' },
    { value: 'stacked_cards', label: 'Stacked', emoji: '📑' },
    { value: 'collage_grid', label: 'Grid', emoji: '🔲' },
    { value: 'tilted_scrapbook', label: 'Tilted', emoji: '📸' },
    { value: 'editorial_framed', label: 'Framed', emoji: '🖼️' },
    { value: 'overlapping_layers', label: 'Overlap', emoji: '🃏' },
    { value: 'top_banner', label: 'Banner', emoji: '🏔️' },
    { value: 'floating_cluster', label: 'Cluster', emoji: '🎈' },
  ];

  const PHOTO_SHAPES: { value: PhotoShape; label: string }[] = [
    { value: 'rectangle', label: 'Sharp' },
    { value: 'rounded', label: 'Rounded' },
    { value: 'circle', label: 'Circle' },
    { value: 'polaroid', label: 'Polaroid' },
  ];

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Photo Layout</Label>
        <div className="grid grid-cols-3 gap-1.5">
          {PHOTO_LAYOUTS.map(({ value, label, emoji }) => (
            <button key={value} onClick={() => updateSettings({ photoLayout: value })}
              className={cn('flex flex-col items-center gap-1 p-2 rounded-lg border transition-all',
                cs.photoLayout === value ? 'border-primary bg-primary/5' : 'border-border')}>
              <span className="text-base">{emoji}</span>
              <span className="text-[9px] text-muted-foreground">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Photo Shape</Label>
        <div className="grid grid-cols-4 gap-1.5">
          {PHOTO_SHAPES.map(({ value, label }) => (
            <button key={value} onClick={() => updateSettings({ photoShape: value })}
              className={cn('py-2 rounded-lg border text-xs font-medium transition-all',
                cs.photoShape === value ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground')}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ====== DECORATIONS PANEL ======
function DecorPanel({ formData, updateSettings }: {
  formData: AnnouncementFormData;
  updateSettings: (u: Partial<CustomTemplateSettings>) => void;
}) {
  const cs = formData.customSettings;

  const DECOR_OPTIONS: { key: keyof DecorationToggles; label: string; emoji: string }[] = [
    { key: 'hearts', label: 'Hearts', emoji: '♥' },
    { key: 'sparkles', label: 'Sparkles', emoji: '✨' },
    { key: 'lines', label: 'Lines', emoji: '━' },
    { key: 'tape', label: 'Tape', emoji: '📎' },
    { key: 'florals', label: 'Florals', emoji: '🌸' },
    { key: 'glow', label: 'Glow', emoji: '💫' },
    { key: 'confetti', label: 'Confetti', emoji: '🎊' },
    { key: 'flourishes', label: 'Flourish', emoji: '〰️' },
  ];

  return (
    <div className="space-y-3">
      <Label className="text-xs text-muted-foreground">Decorative Accents</Label>
      <div className="grid grid-cols-2 gap-2">
        {DECOR_OPTIONS.map(({ key, label, emoji }) => (
          <button key={key}
            onClick={() => updateSettings({ decorations: { ...cs.decorations, [key]: !cs.decorations[key] } })}
            className={cn('flex items-center gap-2 p-3 rounded-lg border transition-all',
              cs.decorations[key] ? 'border-primary bg-primary/5' : 'border-border')}>
            <span className="text-lg">{emoji}</span>
            <span className={cn('text-sm', cs.decorations[key] ? 'text-foreground font-medium' : 'text-muted-foreground')}>
              {label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ====== EMOJI PANEL (existing, preserved) ======
function EmojiPanel({ formData, updateFormData }: {
  formData: AnnouncementFormData;
  updateFormData: (updates: Partial<AnnouncementFormData>) => void;
}) {
  const settings = formData.customSettings;

  const toggleFloating = (type: 'hearts' | 'suns' | 'stars' | 'smileys') => {
    const updated = settings.floatingElements.map((el) =>
      el.type === type ? { ...el, enabled: !el.enabled } : el
    );
    updateFormData({ customSettings: { ...settings, floatingElements: updated } });
  };

  const emojiMap: Record<string, string> = { hearts: '❤️', suns: '☀️', stars: '⭐', smileys: '😊' };

  return (
    <div className="space-y-4">
      <Label className="text-xs text-muted-foreground">Floating Effects</Label>
      <div className="grid grid-cols-2 gap-3">
        {settings.floatingElements.map((el) => (
          <button key={el.type} onClick={() => toggleFloating(el.type)}
            className={cn('flex items-center gap-2 p-3 rounded-lg border transition-all',
              el.enabled ? 'border-primary bg-primary/5' : 'border-border')}>
            <span className="text-lg">{emojiMap[el.type]}</span>
            <span className={cn('text-sm capitalize', el.enabled ? 'text-foreground font-medium' : 'text-muted-foreground')}>
              {el.type}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ====== MAIN DRAWER ======
export function TemplateEditorDrawer({ formData, updateFormData, isEditing, onTogglePreview }: TemplateEditorDrawerProps) {
  const [activePanel, setActivePanel] = useState<EditorPanel>(null);

  const updateSettings = (updates: Partial<CustomTemplateSettings>) => {
    updateFormData({
      customSettings: { ...formData.customSettings, ...updates },
    });
  };

  const tabs: { id: EditorPanel; icon: typeof Paintbrush; label: string }[] = [
    { id: 'layout', icon: LayoutGrid, label: 'Layout' },
    { id: 'background', icon: Paintbrush, label: 'BG' },
    { id: 'text', icon: Type, label: 'Font' },
    { id: 'photos', icon: Camera, label: 'Photos' },
    { id: 'decor', icon: Sparkles, label: 'Decor' },
    { id: 'emoji', icon: Smile, label: 'Emoji' },
  ];

  const panelTitle: Record<string, string> = {
    layout: 'Layout & Sections',
    background: 'Background',
    text: 'Typography',
    photos: 'Photo Layout',
    decor: 'Decorations',
    emoji: 'Floating Effects',
  };

  return (
    <>
      {/* Bottom toolbar */}
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border shadow-lg safe-bottom"
      >
        <div className="flex items-center justify-between px-2 py-1.5 max-w-lg mx-auto">
          <div className="flex gap-0.5 overflow-x-auto">
            {tabs.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setActivePanel(activePanel === id ? null : id)}
                className={cn(
                  'flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-lg transition-all min-w-[44px]',
                  activePanel === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="text-[9px] font-medium">{label}</span>
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={onTogglePreview} className="flex items-center gap-1.5 ml-1 shrink-0">
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Preview</span>
          </Button>
        </div>
      </motion.div>

      {/* Slide-up panel */}
      <AnimatePresence>
        {activePanel && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[38] bg-black/30" onClick={() => setActivePanel(null)} />

            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed bottom-[52px] left-0 right-0 z-[45] bg-background border-t border-border rounded-t-2xl max-h-[55vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-background z-10 pt-3 pb-2 px-4">
                <div className="w-10 h-1 rounded-full bg-muted mx-auto mb-3" />
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-foreground">
                    {panelTitle[activePanel] || ''}
                  </h3>
                  <button onClick={() => setActivePanel(null)} className="p-1.5 rounded-full hover:bg-muted transition-colors">
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              </div>

              <div className="px-4 pb-6">
                {activePanel === 'layout' && <LayoutPanel formData={formData} updateSettings={updateSettings} updateFormData={updateFormData} />}
                {activePanel === 'background' && <BackgroundPanel formData={formData} updateSettings={updateSettings} />}
                {activePanel === 'text' && <TextPanel formData={formData} updateSettings={updateSettings} />}
                {activePanel === 'photos' && <PhotosPanel formData={formData} updateSettings={updateSettings} />}
                {activePanel === 'decor' && <DecorPanel formData={formData} updateSettings={updateSettings} />}
                {activePanel === 'emoji' && <EmojiPanel formData={formData} updateFormData={updateFormData} />}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
